import React, { useEffect, useRef } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useTheme, useMediaQuery } from '@mui/material';

ChartJS.register(...registerables, ChartDataLabels);

const DepartmentDistributionChart = ({ data = [] }) => {
    const chartRef = useRef(null);
    const chartInstance = useRef(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    // Default data if none provided
    const defaultData = [
        { department: 'CSE', count: 50 },
        { department: 'ME', count: 80 },
        { department: 'EE', count: 320 },
        { department: 'ECE', count: 580 },
        { department: 'Civil', count: 460 },
        { department: 'Others', count: 500 },
    ];

    const chartData = data.length > 0 ? data : defaultData;
    const labels = chartData.map(item => item.department);
    const values = chartData.map(item => item.count);
    const maxValue = Math.max(...values, 100);

    useEffect(() => {
        if (chartRef.current) {
            if (chartInstance.current) {
                chartInstance.current.destroy();
            }

            const barThickness = isMobile ? 20 : isTablet ? 26 : 32;
            const fontSize = isMobile ? 10 : isTablet ? 11 : 12;
            const labelFontSize = isMobile ? 11 : isTablet ? 12 : 13;

            // Calculate appropriate step size based on max value
            const stepSize = Math.ceil(maxValue / 4 / 50) * 50 || 50;
            const yMax = Math.ceil(maxValue * 1.2 / stepSize) * stepSize;

            const ctx = chartRef.current.getContext('2d');
            chartInstance.current = new ChartJS(ctx, {
                type: 'bar',
                data: {
                    labels: labels,
                    datasets: [
                        {
                            label: 'Distribution',
                            data: values,
                            backgroundColor: '#D1E0FF',
                            borderColor: '#1142A4',
                            borderWidth: 1,
                            barThickness: barThickness,
                            borderRadius: 2,
                        },
                    ],
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    interaction: {
                        mode: 'index',
                        intersect: false,
                    },
                    plugins: {
                        legend: { display: false },
                        tooltip: {
                            enabled: true,
                            callbacks: {
                                label: (context) => {
                                    const dataItem = chartData[context.dataIndex];
                                    const percentage = dataItem?.percentage || '';
                                    return percentage
                                        ? `Count: ${context.raw} (${percentage})`
                                        : `Count: ${context.raw}`;
                                }
                            }
                        },
                        datalabels: {
                            display: (context) => context.active,
                            align: 'top',
                            anchor: 'end',
                            offset: isMobile ? 2 : 4,
                            color: '#1142A4',
                            font: {
                                family: '"Inter", sans-serif',
                                size: labelFontSize,
                                weight: '700',
                            },
                            formatter: (value) => value.toLocaleString(),
                        },
                    },
                    scales: {
                        x: {
                            grid: { display: true, color: '#f0f0f0' },
                            ticks: {
                                color: '#212121',
                                font: { size: fontSize },
                                maxRotation: isMobile ? 45 : 0,
                                minRotation: isMobile ? 45 : 0,
                            },
                        },
                        y: {
                            min: 0,
                            max: yMax,
                            ticks: {
                                stepSize: stepSize,
                                color: '#212121',
                                font: { size: fontSize },
                                maxTicksLimit: isMobile ? 5 : 10,
                            },
                            border: { display: false },
                            grid: { color: '#f0f0f0' },
                        },
                    },
                },
            });
        }

        return () => {
            if (chartInstance.current) chartInstance.current.destroy();
        };
    }, [isMobile, isTablet, data, labels, values, maxValue, chartData]);

    return <canvas ref={chartRef} style={{ width: '100%', height: '100%' }} />;
};

export default DepartmentDistributionChart;
